import axios from "axios";
import { z } from "zod";
import { toast } from "../../../components/ui/use-toast";
import { CircleCheckBig } from "lucide-react";
import { formatDateToYYYYMMDDLocal } from "../../../utils/dateUtils";
const baseUrl = process.env.REACT_APP_SERVER_URL;

export const getAllActs = async () => {
  try {
    const response = await axios.get(`${baseUrl}/api/acts`);
    return { acts: response.data.acts };
  } catch (error) {
    console.error(error);
  }
};

export const getAllPaginatedActs = async (
  page: number,
  category: string,
  subCategory: string
) => {
  try {
    const response = await axios.get(
      `${baseUrl}/api/acts/all?page=${page}&category=${category}&subCategory=${subCategory}`
    );
    return {
      acts: response.data.acts,
      count: response.data.count,
      totalPages: response.data.totalPages,
    };
  } catch (error) {
    console.error(error);
  }
};

export const handleCreateAct = async (formData: any, navigate: Function) => {
  try {
    const config = {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
        "Content-Type": "multipart/form-data",
      },
    };

    const processedFormData = { ...formData };
    if (processedFormData.date instanceof Date) {
      processedFormData.date = formatDateToYYYYMMDDLocal(processedFormData.date);
    }

    const newsFormData = new FormData();

    for (const key in processedFormData) {
      if (processedFormData.hasOwnProperty(key) && key !== "file") {
        if (processedFormData[key] !== undefined && processedFormData[key] !== null) {
          if (Array.isArray(processedFormData[key])) {
            newsFormData.append(key, JSON.stringify(processedFormData[key]));
          } else {
            newsFormData.append(key, processedFormData[key]);
          }
        }
      }
    }

    if (formData.file) {
      // Validation côté frontend
      const file = formData.file.file;
      if (file.type !== 'application/pdf') {
        toast({
          variant: "destructive",
          title: "Erreur de fichier",
          description: "Seuls les fichiers PDF sont autorisés",
          className: "bg-red-500 border border-red-500",
        });
        return;
      }

      // Vérifier la taille (10MB max)
      if (file.size > 10 * 1024 * 1024) {
        toast({
          variant: "destructive",
          title: "Fichier trop volumineux",
          description: "La taille maximale autorisée est de 10MB",
          className: "bg-red-500 border border-red-500",
        });
        return;
      }

      newsFormData.append(`file`, file);
    }

    const response = await axios.post(
      `${baseUrl}/api/acts/`,
      newsFormData,
      config
    );

    toast({
      variant: "destructive",
      className: "bg-green-500 border border-green-500",
      description: (
        <div className="flex items-center">
          <CircleCheckBig className="mr-2" />
          <span className="first-letter:capitalize">{response.data.msg}</span>
        </div>
      ),
    });
    navigate("/admin-dashboard/mediatheque/actes-administratifs");
  } catch (error: any) {
    if (error instanceof z.ZodError) {
      const fieldErrors: { [key: string]: string } = {};
      error.errors.forEach((err: any) => {
        fieldErrors[err.path[0]] = err.message;
      });
    } else {
      if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        toast({
          variant: "destructive",
          title: "Une erreur s'est produite.",
          description: error.response.data.message,
          className: "bg-red-500 border border-red-500",
        });
        return error.response.data.message;
      }
    }
  }
};

export const handleUpdateAct = async (
  id: number,
  formData: any,
  navigate: Function
) => {
  try {
    const config = {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
        "Content-Type": "multipart/form-data",
      },
    };

    const formDataToSend = new FormData();
    formDataToSend.append("title", formData.title || "");
    formDataToSend.append("category", formData.category || "");
    formDataToSend.append("subCategory", formData.subCategory || "");
    if (formData.date) {
      const dateStr = formData.date instanceof Date
        ? formatDateToYYYYMMDDLocal(formData.date)
        : formData.date;
      formDataToSend.append("date", dateStr);
    }

    if (formData.file) {
      // Validation côté frontend
      const file = formData.file.file;
      if (file.type !== 'application/pdf') {
        toast({
          variant: "destructive",
          title: "Erreur de fichier",
          description: "Seuls les fichiers PDF sont autorisés",
          className: "bg-red-500 border border-red-500",
        });
        return;
      }

      // Vérifier la taille (10MB max)
      if (file.size > 10 * 1024 * 1024) {
        toast({
          variant: "destructive",
          title: "Fichier trop volumineux",
          description: "La taille maximale autorisée est de 10MB",
          className: "bg-red-500 border border-red-500",
        });
        return;
      }

      formDataToSend.append("file", file);
    }

    const response = await axios.patch(
      `${baseUrl}/api/acts/${id}`,
      formDataToSend,
      config
    );
    toast({
      variant: "destructive",
      title: "Opération réussie.",
      description: response.data.msg,
      className: "bg-green-500 border border-green-500",
    });
    navigate("/admin-dashboard/mediatheque/actes-administratifs");
  } catch (error: any) {
    if (error instanceof z.ZodError) {
      const fieldErrors: { [key: string]: string } = {};
      error.errors.forEach((err: any) => {
        fieldErrors[err.path[0]] = err.message;
      });
    } else {
      if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        toast({
          variant: "destructive",
          title: "Une erreur s'est produite.",
          description: error.response.data.message,
          className: "bg-red-500 border border-red-500",
        });
        return error.response.data.message;
      }
    }
  }
};

export const deleteAct = async (id: number) => {
  try {
    const config = {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    };

    const response = await axios.delete(`${baseUrl}/api/acts/${id}`, config);
    toast({
      variant: "destructive",
      className: "bg-green-500 border border-green-500",
      description: (
        <div className="flex items-center">
          <CircleCheckBig className="mr-2" />
          <span className="first-letter:capitalize">
            {response.data.message}
          </span>
        </div>
      ),
    });

    return response;
  } catch (error) {
    console.error(error);
  }
};

export const getActById = async (id: number) => {
  try {
    const response = await axios.get(`${baseUrl}/api/acts/${id}`);

    return response.data;
  } catch (error) {
    console.error(error);
  }
};
